import { useRef, useState } from 'react';
import { addAttachment, deleteAttachment, type TaskAttachment } from '../../lib/tasks';
import { uploadFile } from '../../lib/uploads';
import { Alert, Button, SkeletonRows } from '../ui';
import { PaperclipIcon, TrashIcon } from '../icons';

/** Matches the server's UPLOAD_ATTACHMENT_MAX_BYTES default. Checked here for a
 *  fast, readable failure; the server and the signed URL are the authority. */
const MAX_BYTES = 50 * 1024 * 1024;

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function TaskAttachments({
  taskId,
  attachments,
  placeholderCount = 0,
  onChange,
}: {
  taskId: string;
  attachments: TaskAttachment[];
  /** How many rows are known to exist but have not been fetched yet, from the
   *  list row's `attachmentCount`. Holds the section at its real height while
   *  the detail is in flight, so the button below does not jump. */
  placeholderCount?: number;
  onChange: (attachments: TaskAttachment[]) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function upload(file: File) {
    if (file.size > MAX_BYTES) {
      setError(`That file is larger than ${Math.round(MAX_BYTES / (1024 * 1024))} MB.`);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      // Two steps: the bytes go browser -> S3 first, then the row is recorded.
      // A failure between them leaves only an orphan the lifecycle rule expires.
      const uploaded = await uploadFile(file, 'attachment');
      const { attachment } = await addAttachment(taskId, uploaded);
      onChange([...attachments, attachment]);
    } catch (uploadError: unknown) {
      setError(uploadError instanceof Error ? uploadError.message : 'Could not attach that file.');
    } finally {
      setBusy(false);
      // Reset so the same file can be picked again after a failure.
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function remove(attachmentId: string) {
    try {
      await deleteAttachment(taskId, attachmentId);
      onChange(attachments.filter((entry) => entry.id !== attachmentId));
    } catch (deleteError: unknown) {
      setError(deleteError instanceof Error ? deleteError.message : 'Could not remove that file.');
    }
  }

  return (
    <section>
      <h3 className="text-dense font-semibold tracking-tight text-stone-700">Attachments</h3>

      {error ? (
        <div className="mt-2">
          <Alert>{error}</Alert>
        </div>
      ) : null}

      {attachments.length > 0 ? (
        <ul className="mt-1.5 space-y-0.5">
          {attachments.map((attachment) => (
            <li
              key={attachment.id}
              className="flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-2 py-1"
            >
              <PaperclipIcon className="h-3.5 w-3.5 shrink-0 text-stone-400" />
              {/* A plain link, not a download attribute: the server decides
                  inline vs download in the Content-Disposition it signs. */}
              <a
                href={attachment.downloadUrl}
                target="_blank"
                rel="noreferrer"
                className="min-w-0 flex-1 truncate text-dense text-honey-700 hover:underline"
              >
                {attachment.filename}
              </a>
              <span className="shrink-0 text-meta tabular-nums text-stone-500">
                {formatSize(attachment.size)}
              </span>
              <button
                type="button"
                aria-label={`Remove ${attachment.filename}`}
                className="shrink-0 rounded p-0.5 text-stone-500 transition hover:bg-red-50 hover:text-red-700"
                onClick={() => void remove(attachment.id)}
              >
                <TrashIcon className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      ) : placeholderCount === 0 ? (
        // Says "none" rather than leaving a gap that reads as "still loading".
        // Only once the placeholder count is zero -- while rows are still on
        // their way, the skeleton below is the honest answer.
        <p className="mt-1.5 text-dense text-stone-500">No files attached.</p>
      ) : null}

      <SkeletonRows count={placeholderCount} rowClassName="h-[30px]" />

      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void upload(file);
        }}
      />
      <Button
        compact
        className="mt-1.5"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
      >
        {busy ? 'Uploading…' : 'Attach a file'}
      </Button>
    </section>
  );
}
